import React, { useState, useMemo, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const ContactsTable = ({ 
  contacts, 
  selectedContacts, 
  onSelectContact, 
  onSelectAllContacts, 
  onContactClick, 
  sortConfig, 
  onSort,
  onEdit,
  onStatusUpdate,
  onCall,
  onSms,
  onEmail
}) => {
  const [hoveredRow, setHoveredRow] = useState(null);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setStatusDropdownOpen(null);
      }
    };

    if (statusDropdownOpen) {
      document?.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document?.removeEventListener('mousedown', handleClickOutside);
    };
  }, [statusDropdownOpen]);

  const statusOptions = [
    { value: 'lead', label: 'Lead', color: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
    { value: 'prospect', label: 'Prospect', color: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20' },
    { value: 'customer', label: 'Customer', color: 'bg-green-500/10 text-green-600 border-green-500/20' },
    { value: 'inactive', label: 'Inactive', color: 'bg-gray-500/10 text-gray-600 border-gray-500/20' }
  ];

  const getSortIcon = (column) => {
    if (sortConfig?.key !== column) return 'ArrowUpDown';
    return sortConfig?.direction === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  const handleSort = (key) => {
    onSort?.(key);
  };

  const handleRowClick = (contact, e) => {
    // Don't trigger if clicking on checkbox, status, or action buttons
    if (
      e?.target?.closest('input[type="checkbox"]') ||
      e?.target?.closest('.status-badge') ||
      e?.target?.closest('.action-button')
    ) {
      return;
    }
    onEdit?.(contact);
  };

  const handleStatusClick = (e, contactId) => {
    e?.stopPropagation();
    setStatusDropdownOpen(statusDropdownOpen === contactId ? null : contactId);
  };

  const handleStatusChange = async (contact, newStatus) => {
    setStatusDropdownOpen(null);
    onStatusUpdate?.(contact?.id, newStatus);
  };

  const handleEditClick = (e, contact) => {
    e?.stopPropagation();
    onEdit?.(contact);
  };

  const handleCallClick = (e, contact) => {
    e?.stopPropagation();
    onCall?.(contact);
  };

  const handleSmsClick = (e, contact) => {
    e?.stopPropagation();
    onSms?.(contact);
  };

  const handleEmailClick = (e, contact) => {
    e?.stopPropagation();
    onEmail?.(contact);
  };

  const formatLastContact = (date) => {
    const now = new Date();
    const contactDate = new Date(date);
    const diffTime = Math.abs(now - contactDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    return contactDate?.toLocaleDateString();
  };

  const getStatusColor = (status) => {
    const statusOption = statusOptions?.find(opt => opt?.value === status?.toLowerCase());
    return statusOption?.color || 'bg-muted text-muted-foreground border-border';
  };

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="w-12 px-4 py-3">
                <Checkbox
                  checked={selectedContacts?.length === contacts?.length && contacts?.length > 0}
                  indeterminate={selectedContacts?.length > 0 && selectedContacts?.length < contacts?.length}
                  onCheckedChange={(checked) => onSelectAllContacts?.(checked)}
                />
              </th>
              {[
                { key: 'name', label: 'Contact' },
                { key: 'title', label: 'Title' },
                { key: 'company', label: 'Company' },
                { key: 'email', label: 'Email' },
                { key: 'phone', label: 'Phone' },
                { key: 'lastContact', label: 'Last Contact' },
                { key: 'status', label: 'Status' }
              ]?.map((column) => (
                <th
                  key={column?.key}
                  className="px-4 py-3 text-left text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground"
                  onClick={() => handleSort(column?.key)}
                >
                  <div className="flex items-center space-x-2">
                    <span>{column?.label}</span>
                    <Icon 
                      name={getSortIcon(column?.key)} 
                      size={14} 
                      className="opacity-50"
                    />
                  </div>
                </th>
              ))}
              <th className="w-24 px-4 py-3 text-center text-sm font-medium text-muted-foreground">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {contacts?.map((contact) => (
              <tr
                key={contact?.id}
                className="group hover:bg-muted/30 transition-colors cursor-pointer"
                onMouseEnter={() => setHoveredRow(contact?.id)}
                onMouseLeave={() => setHoveredRow(null)}
                onClick={(e) => handleRowClick(contact, e)}
              >
                <td className="px-4 py-4">
                  <Checkbox
                    checked={selectedContacts?.includes(contact?.id)}
                    onCheckedChange={(checked) => onSelectContact?.(contact?.id, checked)}
                  />
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-muted flex-shrink-0">
                      <Image
                        src={contact?.avatar}
                        alt={contact?.avatarAlt}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <div className="font-medium text-foreground">{contact?.name}</div>
                      <div className="text-sm text-muted-foreground">{contact?.department}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4 text-sm text-foreground">{contact?.title}</td>
                <td className="px-4 py-4 text-sm text-foreground">{contact?.company}</td>
                <td className="px-4 py-4 text-sm text-primary hover:underline">{contact?.email}</td>
                <td className="px-4 py-4 text-sm text-foreground">{contact?.phone}</td>
                <td className="px-4 py-4 text-sm text-muted-foreground">
                  {formatLastContact(contact?.lastContact)}
                </td>
                <td className="px-4 py-4 relative">
                  <div ref={statusDropdownOpen === contact?.id ? dropdownRef : null}>
                    <span 
                      className={`status-badge inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border cursor-pointer hover:opacity-80 ${getStatusColor(contact?.status)}`}
                      onClick={(e) => handleStatusClick(e, contact?.id)}
                    >
                      {contact?.status}
                    </span>
                    {statusDropdownOpen === contact?.id && (
                      <div className="absolute z-50 mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1">
                        {statusOptions?.map((option) => (
                          <button
                            key={option?.value}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center gap-2"
                            onClick={() => handleStatusChange(contact, option?.value)}
                          >
                            <span className={`w-2 h-2 rounded-full ${option?.color?.split(' ')?.[0]?.replace('/10', '')}`}></span>
                            {option?.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center justify-center space-x-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="action-button h-8 w-8"
                      onClick={(e) => handleCallClick(e, contact)}
                      title="Call"
                    >
                      <Icon name="Phone" size={16} className="text-green-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="action-button h-8 w-8"
                      onClick={(e) => handleSmsClick(e, contact)}
                      title="SMS"
                    >
                      <Icon name="MessageSquare" size={16} className="text-blue-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="action-button h-8 w-8"
                      onClick={(e) => handleEmailClick(e, contact)}
                      title="Email"
                    >
                      <Icon name="Mail" size={16} className="text-purple-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="action-button h-8 w-8 hover:bg-muted"
                      onClick={(e) => handleEditClick(e, contact)}
                      title="Edit"
                    >
                      <Icon name="Edit" size={16} className="text-gray-600 hover:text-foreground transition-colors" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Card Layout */}
      <div className="lg:hidden">
        {contacts?.map((contact) => (
          <div
            key={contact?.id}
            className="p-4 border-b border-border last:border-b-0 hover:bg-muted/30 transition-colors cursor-pointer"
            onClick={(e) => handleRowClick(contact, e)}
          >
            <div className="flex items-start space-x-3">
              <Checkbox
                checked={selectedContacts?.includes(contact?.id)}
                onCheckedChange={(checked) => onSelectContact?.(contact?.id, checked)}
                className="mt-1"
              />
              <div className="w-12 h-12 rounded-full overflow-hidden bg-muted flex-shrink-0">
                <Image
                  src={contact?.avatar}
                  alt={contact?.avatarAlt}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-medium text-foreground truncate">{contact?.name}</h3>
                    <p className="text-sm text-muted-foreground">{contact?.title}</p>
                    <p className="text-sm text-muted-foreground">{contact?.company}</p>
                  </div>
                  <div className="relative" ref={statusDropdownOpen === contact?.id ? dropdownRef : null}>
                    <span 
                      className={`status-badge inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border cursor-pointer ${getStatusColor(contact?.status)}`}
                      onClick={(e) => handleStatusClick(e, contact?.id)}
                    >
                      {contact?.status}
                    </span>
                    {statusDropdownOpen === contact?.id && (
                      <div className="absolute z-50 right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1">
                        {statusOptions?.map((option) => (
                          <button
                            key={option?.value}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center gap-2"
                            onClick={() => handleStatusChange(contact, option?.value)}
                          >
                            <span className={`w-2 h-2 rounded-full ${option?.color?.split(' ')?.[0]?.replace('/10', '')}`}></span>
                            {option?.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-2 space-y-1">
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Icon name="Mail" size={14} className="mr-2" />
                    <span className="truncate">{contact?.email}</span>
                  </div>
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Icon name="Phone" size={14} className="mr-2" />
                    <span>{contact?.phone}</span>
                  </div>
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Icon name="Clock" size={14} className="mr-2" />
                    <span>Last contact: {formatLastContact(contact?.lastContact)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ContactsTable;